<template>
  <div class="page-container">
    <div class="page-header">
      <h2>고객사/브랜드 관리</h2>
      <div class="header-actions">
        <el-button type="primary" @click="openDialog()">고객사 추가</el-button>
        <el-button @click="downloadExcel" :loading="excelDownloading">엑셀 다운로드</el-button>
        <el-button @click="openExcelDialog">엑셀 업로드</el-button>
        <el-button @click="showHelp" size="small">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 필터 -->
    <el-card shadow="never" class="filter-card">
      <el-row :gutter="16">
        <el-col :span="6">
          <el-input v-model="searchText" placeholder="고객사명, 사업자번호 검색" clearable
                    @keyup.enter="fetchCustomers" @clear="fetchCustomers" />
        </el-col>
        <el-col :span="4">
          <el-select v-model="filterActive" placeholder="상태" clearable @change="fetchCustomers" style="width:100%">
            <el-option label="사용" :value="true" />
            <el-option label="미사용" :value="false" />
          </el-select>
        </el-col>
      </el-row>
    </el-card>

    <!-- 목록 -->
    <div class="ag-theme-alpine" style="height: 500px; width: 100%; margin-top: 12px;">
      <ag-grid-vue
        :rowData="customers"
        :columnDefs="columnDefs"
        :pagination="true"
        :paginationPageSize="20"
        rowSelection="single"
        @rowClicked="onRowClicked"
        style="width: 100%; height: 100%;"
      />
    </div>

    <!-- 고객사 등록/수정 Dialog -->
    <el-dialog v-model="dialogVisible" :title="form.id ? '고객사 수정' : '고객사 등록'" width="800px"
               :close-on-click-modal="false" destroy-on-close>
      <el-tabs v-model="activeTab">
        <!-- 탭1: 기본정보 -->
        <el-tab-pane label="기본정보" name="basic">
          <el-form :model="form" label-width="110px" ref="formRef">
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="고객사명" prop="name" :rules="[{required:true, message:'필수'}]">
                  <el-input v-model="form.name" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="영문명">
                  <el-input v-model="form.english_name" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="사업자등록번호">
                  <el-input v-model="form.business_number" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="대표자명">
                  <el-input v-model="form.representative_name" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="전화번호">
                  <el-input v-model="form.phone" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="FAX">
                  <el-input v-model="form.fax" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="이메일">
                  <el-input v-model="form.email" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="국가">
                  <el-input v-model="form.country" />
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="주소">
                  <el-input v-model="form.address" />
                </el-form-item>
              </el-col>
            </el-row>

            <el-divider content-position="left">거래 조건</el-divider>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item label="결제조건">
                  <el-input v-model="form.payment_terms" placeholder="예: 납품 후 30일" />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="결제일">
                  <el-input-number v-model="form.payment_day" :min="1" :max="31" placeholder="매월" style="width:100%" />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="기본통화">
                  <el-select v-model="form.currency" style="width:100%">
                    <el-option label="KRW (원)" value="KRW" />
                    <el-option label="USD (달러)" value="USD" />
                    <el-option label="EUR (유로)" value="EUR" />
                    <el-option label="JPY (엔)" value="JPY" />
                    <el-option label="CNY (위안)" value="CNY" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="고객사구분">
                  <el-input v-model="form.category" placeholder="예: 백화점, 온라인" />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="사용여부">
                  <el-switch v-model="form.is_active" active-text="사용" inactive-text="미사용" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item label="비고">
                  <el-input v-model="form.memo" type="textarea" :rows="2" />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </el-tab-pane>

        <!-- 탭2: 계좌정보 -->
        <el-tab-pane label="계좌정보" name="accounts">
          <el-table :data="form.accounts" size="small" border style="width:100%">
            <el-table-column label="은행명" min-width="120">
              <template #default="{ row }">
                <el-input v-model="row.bank_name" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="계좌번호" min-width="160">
              <template #default="{ row }">
                <el-input v-model="row.account_number" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="예금주" min-width="100">
              <template #default="{ row }">
                <el-input v-model="row.account_holder" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="구분" width="120">
              <template #default="{ row }">
                <el-select v-model="row.account_type" size="small" style="width:100%">
                  <el-option label="원화" value="KRW" />
                  <el-option label="외화" value="FOREIGN" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="" width="70" align="center">
              <template #default="{ $index }">
                <el-button type="danger" size="small" text @click="form.accounts.splice($index, 1)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button size="small" style="margin-top:8px"
                     @click="form.accounts.push({bank_name:'',account_number:'',account_holder:'',account_type:'KRW'})">
            + 계좌 추가
          </el-button>
        </el-tab-pane>

        <!-- 탭3: 브랜드 -->
        <el-tab-pane label="브랜드" name="brands">
          <el-table :data="form.brands" size="small" border style="width:100%">
            <el-table-column label="브랜드명" min-width="140">
              <template #default="{ row }">
                <el-input v-model="row.name" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="영문명" min-width="120">
              <template #default="{ row }">
                <el-input v-model="row.english_name" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="담당자" min-width="100">
              <template #default="{ row }">
                <el-input v-model="row.contact_person" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="전화번호" min-width="120">
              <template #default="{ row }">
                <el-input v-model="row.contact_phone" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="이메일" min-width="140">
              <template #default="{ row }">
                <el-input v-model="row.contact_email" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="담당법인" min-width="135">
              <template #default="{ row }">
                <template v-if="row.id">
                  <el-tag v-if="row.managing_company_name" size="small">{{ row.managing_company_name }}</el-tag>
                  <span v-else class="muted-txt">미지정</span>
                  <el-button size="small" text type="primary" @click="openCompanyDialog(row)">변경</el-button>
                </template>
                <span v-else class="muted-txt">저장 후 지정</span>
              </template>
            </el-table-column>
            <el-table-column label="" width="70" align="center">
              <template #default="{ $index }">
                <el-button type="danger" size="small" text @click="form.brands.splice($index, 1)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button size="small" style="margin-top:8px"
                     @click="form.brands.push({name:'',english_name:'',contact_person:'',contact_phone:'',contact_email:''})">
            + 브랜드 추가
          </el-button>
        </el-tab-pane>

        <!-- 탭4: 물류주소 -->
        <el-tab-pane label="물류주소" name="logistics" :disabled="!form.id">
          <template v-if="form.id">
            <div v-for="brand in savedBrands" :key="brand.id" style="margin-bottom: 20px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <h4 style="margin: 0; color: #303133;">{{ brand.name }}</h4>
                <el-button size="small" @click="openAddressDialog(brand)">+ 주소 추가</el-button>
              </div>
              <el-table :data="logisticsAddresses[brand.id] || []" size="small" border style="width:100%">
                <el-table-column prop="address_type_display" label="구분" width="100" />
                <el-table-column prop="address" label="주소" min-width="200" />
                <el-table-column prop="contact_name" label="담당자" width="100" />
                <el-table-column prop="contact_phone" label="연락처" width="130" />
                <el-table-column label="기본" width="70" align="center">
                  <template #default="{ row }">
                    <el-tag v-if="row.is_default" type="success" size="small">기본</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="" width="70" align="center">
                  <template #default="{ row }">
                    <el-button type="danger" size="small" text @click="deleteAddress(brand.id, row.id)">삭제</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <el-empty v-if="!logisticsAddresses[brand.id] || logisticsAddresses[brand.id].length === 0"
                        description="등록된 물류주소가 없습니다" :image-size="40" />
            </div>
            <el-empty v-if="savedBrands.length === 0" description="저장된 브랜드가 없습니다. 먼저 브랜드를 등록 후 저장해주세요." />
          </template>
          <el-empty v-else description="고객사 저장 후 물류주소를 관리할 수 있습니다." />
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <el-button @click="dialogVisible = false">취소</el-button>
        <el-button type="primary" @click="save">저장</el-button>
      </template>
    </el-dialog>

    <!-- 물류주소 추가 다이얼로그 -->
    <el-dialog v-model="addressDialogVisible" title="물류주소 추가" width="500px" :close-on-click-modal="false" destroy-on-close append-to-body>
      <el-form :model="addressForm" label-width="90px" ref="addressFormRef">
        <el-form-item label="구분" prop="address_type" :rules="[{required:true, message:'필수'}]">
          <el-select v-model="addressForm.address_type" style="width:100%">
            <el-option label="물류창고" value="WAREHOUSE" />
            <el-option label="매장" value="STORE" />
            <el-option label="사무실" value="OFFICE" />
          </el-select>
        </el-form-item>
        <el-form-item label="주소" prop="address" :rules="[{required:true, message:'필수'}]">
          <el-input v-model="addressForm.address" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="담당자">
          <el-input v-model="addressForm.contact_name" />
        </el-form-item>
        <el-form-item label="연락처">
          <el-input v-model="addressForm.contact_phone" />
        </el-form-item>
        <el-form-item label="입고규칙">
          <el-input v-model="addressForm.receiving_rules" type="textarea" :rows="2" placeholder="입고 시 유의사항" />
        </el-form-item>
        <el-form-item label="기본주소">
          <el-switch v-model="addressForm.is_default" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addressDialogVisible = false">취소</el-button>
        <el-button type="primary" @click="saveAddress" :loading="addressSaving">저장</el-button>
      </template>
    </el-dialog>

    <!-- 담당법인 변경 다이얼로그 (customer-app-design.md §8.4) -->
    <el-dialog v-model="companyDialogVisible" title="브랜드 담당법인 변경" width="480px"
               :close-on-click-modal="false" destroy-on-close append-to-body>
      <div class="cc-brand-line">
        <b>{{ companyTarget?.name }}</b>
        <span class="muted-txt"> 현재: {{ companyTarget?.managing_company_name || '미지정' }}</span>
      </div>
      <el-form label-width="90px">
        <el-form-item label="새 담당법인" required>
          <el-select v-model="companyForm.company" style="width:100%" placeholder="법인 선택">
            <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="적용일">
          <el-date-picker v-model="companyForm.start_date" type="date" value-format="YYYY-MM-DD" style="width:100%" />
        </el-form-item>
        <el-form-item label="변경 사유">
          <el-input v-model="companyForm.note" maxlength="200" placeholder="예: 사업부 이관" />
        </el-form-item>
        <el-form-item label-width="0">
          <el-checkbox v-model="companyForm.deactivate_assignments">
            타법인 소속이 되는 기존 담당자를 일괄 비활성
          </el-checkbox>
        </el-form-item>
      </el-form>
      <div class="cc-hint">
        변경 후 기존 담당자 중 새 법인 소속이 아닌 인원은 '법인 불일치'로 표시됩니다.
        브랜드 담당자 관리에서 새 법인 직원으로 재배정하세요.
      </div>
      <div v-if="companyHistories.length" class="cc-history">
        <div class="cc-history-title">변경 이력</div>
        <div v-for="h in companyHistories" :key="h.id" class="cc-history-row">
          {{ h.start_date }} ~ {{ h.end_date || '현재' }} · {{ h.company_name }}
          <span v-if="h.note" class="muted-txt"> — {{ h.note }}</span>
        </div>
      </div>
      <template #footer>
        <el-button @click="companyDialogVisible = false">취소</el-button>
        <el-button type="primary" :loading="companySaving" @click="submitCompanyChange">변경</el-button>
      </template>
    </el-dialog>

    <!-- 엑셀 업로드 다이얼로그 (검증 → 반영 2단계) -->
    <el-dialog v-model="excelDialogVisible" title="고객사/브랜드 엑셀 업로드" width="620px"
               :close-on-click-modal="false" destroy-on-close>
      <div class="cc-hint">
        [엑셀 다운로드] 서식 그대로 수정/행 추가 후 업로드하세요.
        ID가 있으면 갱신, 없으면 이름으로 찾고, 못 찾으면 신규 등록됩니다.
        빈 칸은 변경되지 않습니다. 담당법인은 엑셀로 변경할 수 없습니다(이력 관리).
      </div>
      <input type="file" accept=".xlsx" @change="onExcelFileChange" style="margin: 10px 0;" />

      <template v-if="excelReport">
        <el-alert :type="excelReportHasError ? 'warning' : 'success'" :closable="false" show-icon
                  :title="excelReport.dry_run ? '검증 결과 (아직 반영 안 됨)' : '반영 완료'">
          <div class="xl-report">
            <div><b>고객사</b> — 신규 {{ excelReport.customers.created.length }} ·
              수정 {{ excelReport.customers.updated.length }} ·
              오류 {{ excelReport.customers.errors.length }}</div>
            <div><b>브랜드</b> — 신규 {{ excelReport.brands.created.length }} ·
              수정 {{ excelReport.brands.updated.length }} ·
              오류 {{ excelReport.brands.errors.length }}</div>
          </div>
        </el-alert>
        <div v-if="excelReport.customers.created.length" class="xl-detail">
          신규 고객사: {{ excelReport.customers.created.join(', ') }}</div>
        <div v-if="excelReport.brands.created.length" class="xl-detail">
          신규 브랜드: {{ excelReport.brands.created.join(', ') }}</div>
        <div v-if="excelReport.customers.updated.length" class="xl-detail">
          수정 고객사: {{ excelReport.customers.updated.join(', ') }}</div>
        <div v-if="excelReport.brands.updated.length" class="xl-detail">
          수정 브랜드: {{ excelReport.brands.updated.join(', ') }}</div>
        <div v-for="(err, i) in [...excelReport.customers.errors, ...excelReport.brands.errors]"
             :key="i" class="xl-error">⚠ {{ err }}</div>
      </template>

      <template #footer>
        <el-button @click="excelDialogVisible = false">닫기</el-button>
        <el-button type="primary" plain :disabled="!excelFile" :loading="excelUploading"
                   @click="uploadExcel(false)">검증</el-button>
        <el-button type="primary"
                   :disabled="!excelFile || !excelReport || !excelReport.dry_run || excelReportHasError"
                   :loading="excelUploading" @click="uploadExcel(true)">반영</el-button>
      </template>
    </el-dialog>

    <HelpPanel v-model="helpVisible" :content-path="'screens/customer-list'" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import { QuestionFilled } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { ElMessage } from 'element-plus'
import HelpPanel from '@/components/help/HelpPanel.vue'

const customers = ref([])
const searchText = ref('')
const filterActive = ref(null)
const dialogVisible = ref(false)
const activeTab = ref('basic')
const formRef = ref()
const helpVisible = ref(false)
const showHelp = () => { helpVisible.value = true }

const emptyForm = () => ({
  id: null,
  name: '', english_name: '',
  business_number: '', representative_name: '',
  country: '', address: '',
  phone: '', fax: '', email: '',
  payment_terms: '', payment_day: null, currency: 'KRW',
  category: '', is_active: true, memo: '',
  accounts: [],
  brands: [],
})

const form = ref(emptyForm())

// 물류주소 관련
const logisticsAddresses = ref({})
const addressDialogVisible = ref(false)
const addressSaving = ref(false)
const addressFormRef = ref(null)
const addressBrandId = ref(null)
const addressForm = ref({
  address_type: 'WAREHOUSE',
  address: '',
  contact_name: '',
  contact_phone: '',
  receiving_rules: '',
  is_default: false,
})

// 저장된 브랜드만 (id가 있는 것들)
const savedBrands = computed(() => (form.value.brands || []).filter(b => b.id))

const columnDefs = ref([
  { field: 'name', headerName: '고객사명', minWidth: 150, flex: 1 },
  { field: 'english_name', headerName: '영문명', minWidth: 120 },
  { field: 'business_number', headerName: '사업자번호', minWidth: 120 },
  { field: 'representative_name', headerName: '대표자', minWidth: 100 },
  { field: 'phone', headerName: '연락처', minWidth: 120 },
  { field: 'brand_count', headerName: '브랜드수', width: 90, cellStyle: { textAlign: 'center' } },
  { field: 'category', headerName: '구분', width: 100 },
  { field: 'is_active', headerName: '상태', width: 80, cellStyle: { textAlign: 'center' },
    valueFormatter: p => p.value ? '사용' : '미사용' },
])

const fetchCustomers = async () => {
  try {
    const params = {}
    if (searchText.value) params.search = searchText.value
    if (filterActive.value !== null) params.is_active = filterActive.value
    const res = await axiosInstance.get('/customers/', { params })
    customers.value = res.data.results || res.data || []
  } catch (err) {
    console.error('고객사 목록 조회 실패:', err)
  }
}

const openDialog = async (customer = null) => {
  activeTab.value = 'basic'
  logisticsAddresses.value = {}
  if (customer) {
    try {
      const res = await axiosInstance.get(`/customers/${customer.id}/`)
      form.value = { ...emptyForm(), ...res.data }
    } catch (err) {
      console.error('고객사 상세 조회 실패:', err)
      form.value = { ...emptyForm(), ...customer, accounts: [], brands: [] }
    }
    // 물류주소 비동기 조회
    fetchLogisticsAddresses()
  } else {
    form.value = emptyForm()
  }
  dialogVisible.value = true
}

const onRowClicked = (event) => {
  openDialog(event.data)
}

const save = async () => {
  try {
    if (formRef.value) {
      await formRef.value.validate()
    }

    const payload = { ...form.value }
    const accounts = payload.accounts || []
    const brands = payload.brands || []
    delete payload.accounts
    delete payload.brands
    delete payload.brand_count

    let customerId = form.value.id
    if (customerId) {
      await axiosInstance.put(`/customers/${customerId}/`, payload)
    } else {
      const res = await axiosInstance.post('/customers/', payload)
      customerId = res.data.id
    }

    // 계좌 저장
    const validAccounts = accounts.filter(a => a.bank_name && a.account_number)
    await axiosInstance.post(`/customers/${customerId}/accounts/`, validAccounts)

    // 브랜드 저장
    const validBrands = brands.filter(b => b.name)
    if (validBrands.length > 0) {
      await axiosInstance.post(`/customers/${customerId}/brands/`, validBrands)
    }

    ElMessage.success(form.value.id ? '수정되었습니다' : '등록되었습니다')
    dialogVisible.value = false
    fetchCustomers()
  } catch (err) {
    if (err && err !== false) {
      console.error('저장 오류:', err)
      ElMessage.error('저장 중 오류가 발생했습니다')
    }
  }
}

// 물류주소 조회 (브랜드별)
const fetchLogisticsAddresses = async () => {
  const brands = savedBrands.value
  if (brands.length === 0) {
    logisticsAddresses.value = {}
    return
  }
  const result = {}
  await Promise.all(brands.map(async (brand) => {
    try {
      const res = await axiosInstance.get(`/brands/${brand.id}/logistics-addresses/`)
      result[brand.id] = res.data || []
    } catch (err) {
      console.error(`물류주소 조회 실패 (brand=${brand.id}):`, err)
      result[brand.id] = []
    }
  }))
  logisticsAddresses.value = result
}

const openAddressDialog = (brand) => {
  addressBrandId.value = brand.id
  addressForm.value = {
    address_type: 'WAREHOUSE',
    address: '',
    contact_name: '',
    contact_phone: '',
    receiving_rules: '',
    is_default: false,
  }
  addressDialogVisible.value = true
}

const saveAddress = async () => {
  if (addressFormRef.value) {
    try {
      await addressFormRef.value.validate()
    } catch { return }
  }
  addressSaving.value = true
  try {
    await axiosInstance.post(`/brands/${addressBrandId.value}/logistics-addresses/`, addressForm.value)
    ElMessage.success('물류주소가 등록되었습니다')
    addressDialogVisible.value = false
    await fetchLogisticsAddresses()
  } catch (err) {
    console.error('물류주소 저장 실패:', err)
    ElMessage.error('물류주소 저장 중 오류가 발생했습니다')
  } finally {
    addressSaving.value = false
  }
}

const deleteAddress = async (brandId, addressId) => {
  try {
    await axiosInstance.delete(`/brands/${brandId}/logistics-addresses/${addressId}/`)
    ElMessage.success('삭제되었습니다')
    await fetchLogisticsAddresses()
  } catch (err) {
    console.error('물류주소 삭제 실패:', err)
    ElMessage.error('삭제 중 오류가 발생했습니다')
  }
}

// ── 브랜드 담당법인 변경 (customer-app-design.md §8) ──
const companies = ref([])
const companyDialogVisible = ref(false)
const companySaving = ref(false)
const companyTarget = ref(null)
const companyHistories = ref([])
const companyForm = ref({ company: null, start_date: '', note: '', deactivate_assignments: false })

const fetchCompanies = async () => {
  try {
    const res = await axiosInstance.get('/expense-company/')
    companies.value = res.data?.results || res.data || []
  } catch (e) {
    console.error('법인 목록 조회 실패:', e)
  }
}

const openCompanyDialog = async (row) => {
  companyTarget.value = row
  companyForm.value = {
    company: null,
    start_date: new Date().toISOString().slice(0, 10),
    note: '',
    deactivate_assignments: false,
  }
  companyHistories.value = []
  companyDialogVisible.value = true
  try {
    const res = await axiosInstance.get(`/brands/${row.id}/company-history/`)
    companyHistories.value = res.data || []
  } catch { /* 이력 조회 실패는 무시 */ }
}

const submitCompanyChange = async () => {
  if (!companyForm.value.company) {
    ElMessage.warning('새 담당법인을 선택하세요.')
    return
  }
  companySaving.value = true
  try {
    const res = await axiosInstance.post(
      `/brands/${companyTarget.value.id}/change-company/`, companyForm.value
    )
    companyTarget.value.managing_company = res.data.managing_company
    companyTarget.value.managing_company_name = res.data.managing_company_name
    companyDialogVisible.value = false
    let msg = `담당법인이 ${res.data.managing_company_name}(으)로 변경되었습니다.`
    if (res.data.deactivated_count) msg += ` 기존 담당자 ${res.data.deactivated_count}명 비활성.`
    ElMessage.success(msg)
    if (res.data.mismatch_count) {
      ElMessage.warning(
        `법인 불일치 담당자 ${res.data.mismatch_count}명 — 브랜드 담당자 관리에서 재배정하세요.`
      )
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '담당법인 변경에 실패했습니다.')
  } finally {
    companySaving.value = false
  }
}

// ── 엑셀 다운로드/업로드 (2026-07-17) ──
const excelDownloading = ref(false)
const excelDialogVisible = ref(false)
const excelUploading = ref(false)
const excelFile = ref(null)
const excelReport = ref(null)

const excelReportHasError = computed(() =>
  !!excelReport.value &&
  (excelReport.value.customers.errors.length > 0 || excelReport.value.brands.errors.length > 0)
)

const downloadExcel = async () => {
  excelDownloading.value = true
  try {
    const res = await axiosInstance.get('/customers/excel-export/', { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `고객사브랜드_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error('엑셀 다운로드 실패:', e)
    ElMessage.error('엑셀 다운로드에 실패했습니다')
  } finally {
    excelDownloading.value = false
  }
}

const openExcelDialog = () => {
  excelFile.value = null
  excelReport.value = null
  excelDialogVisible.value = true
}

const onExcelFileChange = (e) => {
  excelFile.value = e.target.files?.[0] || null
  excelReport.value = null // 파일이 바뀌면 이전 검증 결과 무효화
}

const uploadExcel = async (apply) => {
  if (!excelFile.value) return
  excelUploading.value = true
  try {
    const fd = new FormData()
    fd.append('file', excelFile.value)
    const res = await axiosInstance.post(
      `/customers/excel-import/${apply ? '?apply=1' : ''}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    excelReport.value = res.data
    if (apply) {
      ElMessage.success('반영되었습니다')
      fetchCustomers()
    }
  } catch (e) {
    console.error('엑셀 업로드 실패:', e)
    ElMessage.error(e.response?.data?.detail || '엑셀 업로드에 실패했습니다')
  } finally {
    excelUploading.value = false
  }
}

onMounted(() => {
  fetchCustomers()
  fetchCompanies()
})
</script>

<style scoped>
.page-container {
  padding: 20px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.page-header h2 {
  margin: 0;
}
.filter-card {
  margin-bottom: 0;
}
.muted-txt { color: #c0c4cc; font-size: 12px; }
.cc-brand-line { margin-bottom: 12px; font-size: 14px; }
.cc-hint {
  background: #f4f4f5; color: #909399; border-radius: 6px;
  padding: 8px 10px; font-size: 12px; margin-bottom: 10px;
}
.cc-history { border-top: 1px solid #ebeef5; padding-top: 8px; }
.cc-history-title { font-size: 12px; font-weight: 600; color: #606266; margin-bottom: 4px; }
.cc-history-row { font-size: 12px; color: #606266; padding: 2px 0; }
.xl-report { font-size: 13px; line-height: 1.7; }
.xl-detail { font-size: 12px; color: #606266; margin-top: 6px; word-break: break-all; }
.xl-error { font-size: 12px; color: #e6a23c; margin-top: 4px; }
</style>
